import React, { useEffect, useRef } from "react";
import { gql, useQuery } from "@apollo/client";
import { useLocation } from "react-router-dom";
import { useIntercom } from "react-use-intercom";
import { isImpersonating, withAuthProvider } from "../../lib/auth";
import { COACH_TOOLS_API } from "../apolloClient";

export const INTERCOM_USER_JWT_QUERY = gql`
  query IntercomUserJwt {
    currentMember {
      intercomUserJwt(platform: WEB)
    }
  }
`;

export const resolveSession = ({
  authReady,
  impersonating,
  isLoggedIn,
  jwtLoading,
  intercomUserJwt,
  loggingOut,
}) => {
  if (!authReady) return { kind: "pending" };
  if (impersonating || loggingOut) return { kind: "off" };
  if (isLoggedIn && jwtLoading && !intercomUserJwt) {
    return { kind: "pending" };
  }
  if (isLoggedIn && intercomUserJwt) {
    return { kind: "verified", jwt: intercomUserJwt };
  }
  // Settled empty JWT or GraphQL error: fail open to anonymous messenger
  // (same contract as directory / account / events Intercom JWT migrations).
  return { kind: "anonymous" };
};

export const Intercom = ({ authReady = false, isLoggedIn }) => {
  const { boot, shutdown, hardShutdown } = useIntercom();
  const { pathname } = useLocation();
  const loggingOut = pathname === "/logout";
  const impersonating = isImpersonating();
  const { data, loading: jwtLoading } = useQuery(INTERCOM_USER_JWT_QUERY, {
    context: { api: COACH_TOOLS_API },
    skip: !authReady || !isLoggedIn || impersonating || loggingOut,
  });
  const intercomUserJwt = data?.currentMember?.intercomUserJwt;
  const { kind, jwt } = resolveSession({
    authReady,
    impersonating,
    isLoggedIn,
    jwtLoading,
    intercomUserJwt,
    loggingOut,
  });
  const appliedSessionRef = useRef(null);

  useEffect(() => {
    if (kind === "pending") {
      return;
    }

    const previous = appliedSessionRef.current;
    if (
      previous &&
      previous.kind === kind &&
      (kind !== "verified" || previous.jwt === jwt)
    ) {
      return;
    }

    if (kind === "off") {
      hardShutdown();
    } else {
      // Leaving verified must clear Intercom identity cookies; soft shutdown
      // can leave them and let a later anonymous boot keep the prior user.
      if (previous?.kind === "verified") {
        hardShutdown();
      } else if (previous && previous.kind !== "off") {
        shutdown();
      }
      if (kind === "verified") {
        boot({ intercomUserJwt: jwt });
      } else {
        boot();
      }
    }

    appliedSessionRef.current = { kind, jwt };
  }, [kind, jwt, boot, shutdown, hardShutdown]);

  return null;
};

const IntercomWithAuth = withAuthProvider(Intercom);

IntercomWithAuth.displayName = "withAuthProvider(Intercom)";
export default IntercomWithAuth;
